import { Button } from '@heroui/react'
import { X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Drawer } from 'vaul'
import { useTranslations } from 'use-intl'
import { AdvancedFilters } from '../search/AdvancedFilters'
import { BudgetFilter } from '../search/BudgetFilter'
import { GlobalSearchBar } from '../search/GlobalSearchBar'
import { CategoryChips } from './CategoryChips'

interface MobileDiscoveryFilterSheetProps {
  isOpen: boolean
  initialSection?: MobileDiscoveryFilterSection
  activeFilterCount: number
  selectedCategory: string
  favoritesOnly?: boolean
  onClose: () => void
  onReset: () => void
  onSelectCategory: (id: string) => void
  onToggleFavorites: (value: boolean) => void
}

export type MobileDiscoveryFilterSection = 'search' | 'filters'

export function MobileDiscoveryFilterSheet({
  isOpen,
  initialSection = 'filters',
  activeFilterCount,
  selectedCategory,
  favoritesOnly,
  onClose,
  onReset,
  onSelectCategory,
  onToggleFavorites,
}: Readonly<MobileDiscoveryFilterSheetProps>) {
  const t = useTranslations('discovery.mobileFilters')
  const scrollRef = useRef<HTMLDivElement>(null)
  const searchSectionRef = useRef<HTMLElement>(null)
  const filterSectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!isOpen) return

    window.requestAnimationFrame(() => {
      const targetSection = initialSection === 'search'
        ? searchSectionRef.current
        : filterSectionRef.current

      targetSection?.scrollIntoView?.({ block: 'start' })

      if (initialSection === 'search') {
        searchSectionRef.current
          ?.querySelector<HTMLInputElement>('[data-testid="global-search-input"]')
          ?.focus()
      }
    })
  }, [initialSection, isOpen])

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      modal
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-[70] bg-black/40" />
        <Drawer.Content
          className="fixed inset-x-0 bottom-0 z-[80] flex max-h-[92dvh] flex-col rounded-t-2xl bg-white text-stone-900 shadow-2xl outline-none dark:bg-stone-950 dark:text-stone-100"
          data-testid="mobile-filter-sheet"
        >
          <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-stone-300 dark:bg-stone-700" />
          <div className="flex items-start justify-between gap-4 px-5 py-4">
            <div>
              <Drawer.Title className="text-lg font-bold">{t('title')}</Drawer.Title>
              <Drawer.Description className="mt-1 text-sm text-stone-500 dark:text-stone-400">
                {t('description')}
              </Drawer.Description>
              {activeFilterCount > 0 && (
                <p className="mt-2 text-xs font-semibold text-primary">
                  {t('activeCount', { count: activeFilterCount })}
                </p>
              )}
            </div>
            <Button
              isIconOnly
              aria-label={t('close')}
              variant="ghost"
              className="h-11 min-w-11 rounded-full"
              onPress={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 pb-5" ref={scrollRef}>
            <section aria-labelledby="mobile-filter-search" ref={searchSectionRef}>
              <h3 id="mobile-filter-search" className="mb-2 text-xs font-bold uppercase text-stone-500">
                {t('searchSection')}
              </h3>
              <GlobalSearchBar />
            </section>

            <section aria-labelledby="mobile-filter-budget" ref={filterSectionRef}>
              <h3 id="mobile-filter-budget" className="sr-only">
                {t('budgetSection')}
              </h3>
              <BudgetFilter />
            </section>

            <section aria-labelledby="mobile-filter-advanced">
              <h3 id="mobile-filter-advanced" className="mb-2 text-xs font-bold uppercase text-stone-500">
                {t('advancedSection')}
              </h3>
              <AdvancedFilters />
            </section>

            <section aria-labelledby="mobile-filter-categories">
              <h3 id="mobile-filter-categories" className="mb-2 text-xs font-bold uppercase text-stone-500">
                {t('categoriesSection')}
              </h3>
              <CategoryChips
                selectedId={selectedCategory}
                onSelect={onSelectCategory}
                favoritesOnly={favoritesOnly}
                onToggleFavorites={onToggleFavorites}
                compact
              />
            </section>
          </div>

          <div className="flex shrink-0 gap-3 border-t border-stone-200 bg-white px-5 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-4 dark:border-stone-800 dark:bg-stone-950">
            <Button variant="outline" className="h-11 flex-1 rounded-xl" onPress={onReset}>
              {t('reset')}
            </Button>
            <Button variant="primary" className="h-11 flex-1 rounded-xl font-semibold" onPress={onClose}>
              {t('apply')}
            </Button>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
